import {Beaker, Dna, TestTubeDiagonal} from 'lucide-react'
import { Link } from 'react-router-dom'
import CellDoublingTimeCalculator from '@/components/calculators/CellDoublingTimeCalculator'
import MolarityDilutionCalculator from '@/components/calculators/MolarityDilutionCalculator'
import QpcrCopyNumberCalculator from '@/components/calculators/QpcrCopyNumberCalculator'

export type CalcTab = 'molarity' | 'doubling' | 'qpcr'

const CALC_TABS: { id: CalcTab, label: string, icon: typeof Beaker }[] = [
  { id: 'molarity', label: 'Molarity & Dilution', icon: Beaker },
  { id: 'doubling', label: 'Cell Doubling Time', icon: Dna },
  { id: 'qpcr', label: 'qPCR Copy Number', icon: TestTubeDiagonal },
]

interface CalculatorSuiteProps {
  activeCalc: CalcTab
}

function CalculatorSuite({ activeCalc }: CalculatorSuiteProps) {
  return (
    <section aria-labelledby="calc-suite-heading">
      <h1 id="calc-suite-heading" className="text-3xl font-extrabold text-slate-900 mb-2">
        Lab Calculator Suite
      </h1>
      <p className="text-slate-600 mb-6 max-w-2xl">
        Three precision calculators for common laboratory workflows: molarity dilution, cell culture doubling time, and qPCR DNA copy number. All calculations run instantly in your browser. Each calculator has its own shareable URL for direct access.
      </p>

      <div
        role="tablist"
        aria-label="Calculator selection"
        className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-1"
      >
        {CALC_TABS.map(({ id, label, icon: Icon }) => {
          const isActive = activeCalc === id
          return (
            <Link
              key={id}
              to={`/calculators/${id}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`calc-panel-${id}`}
              id={`calc-tab-${id}`}
              className={
                isActive
                  ? 'inline-flex items-center gap-2 px-4 py-2.5 rounded-t-md text-sm font-semibold bg-white border border-slate-200 border-b-white text-emerald-700 -mb-px'
                  : 'inline-flex items-center gap-2 px-4 py-2.5 rounded-t-md text-sm font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors'
              }
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </Link>
          )
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-8 shadow-sm">
        <div
          id="calc-panel-molarity"
          role="tabpanel"
          aria-labelledby="calc-tab-molarity"
          hidden={activeCalc !== 'molarity'}
        >
          <MolarityDilutionCalculator />
        </div>
        <div
          id="calc-panel-doubling"
          role="tabpanel"
          aria-labelledby="calc-tab-doubling"
          hidden={activeCalc !== 'doubling'}
        >
          <CellDoublingTimeCalculator />
        </div>
        <div
          id="calc-panel-qpcr"
          role="tabpanel"
          aria-labelledby="calc-tab-qpcr"
          hidden={activeCalc !== 'qpcr'}
        >
          <QpcrCopyNumberCalculator />
        </div>
      </div>
    </section>
  )
}

export default CalculatorSuite
